import React, { useState, useEffect, useRef } from 'react';
import AppLayout from '../components/AppLayout';
import authFetch from '../api/authFetch';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler } from 'chart.js';
import { Line } from 'react-chartjs-2';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import './MarketInsight.css';

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler, ChartDataLabels);

const HIGHLIGHT_LAST_SHOWN_KEY = 'marketInsight.highlightLastShown';
const getTodayKey = () => new Date().toISOString().split('T')[0];

const MarketInsight = () => {
  // ═══ STATE MANAGEMENT ═══
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  
  // Date management
  const [allDates, setAllDates] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  
  // Filter states
  const [selectedYear, setSelectedYear] = useState('');
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedCollection, setSelectedCollection] = useState('nifty50');
  
  // Data states
  const [summaryData, setSummaryData] = useState({
    advance: { count: 0, totalCsValue: 0 },
    decline: { count: 0, totalCsValue: 0 },
    unchanged: { count: 0, totalCsValue: 0 },
    netCsValue: 0,
    totalStocks: 0
  });
  
  const [bullishStocks, setBullishStocks] = useState([]);
  const [bearishStocks, setBearishStocks] = useState([]);
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  // Highlight toasts state
  const [highlightToasts, setHighlightToasts] = useState([]);
  const [highlightsLoading, setHighlightsLoading] = useState(false);

  // Copy-symbols state
  const [copiedTable, setCopiedTable] = useState('');

  // Mobile/tablet filter bar visibility
  const [filterOpen, setFilterOpen] = useState(false);

  const chartRef = useRef(null);
  const highlightRequestRef = useRef(false);
  const autoHighlightRef = useRef(false);

  // Build cs-calendar URL for a given symbol
  const calendarUrl = (symbol) => {
    const y = selectedYear || new Date().getFullYear();
    const m = selectedMonth || (new Date().getMonth() + 1);
    return `/cs-calendar?symbol=${encodeURIComponent(symbol)}&year=${y}&month=${m}&collection=${selectedCollection}`;
  };

  // ═══ COPY SYMBOLS FOR TRADINGVIEW ═══
  const copySymbols = (stocks, table) => {
    if (!stocks.length) return;
    const text = stocks.map(s => `NSE:${s.symbol}`).join(',');
    navigator.clipboard.writeText(text).catch(() => {
      // fallback for browsers that block clipboard
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
    });
    setCopiedTable(table);
    setTimeout(() => setCopiedTable(''), 2000);
  };

  // Dynamic API URL - works for both localhost and network access
  const getApiBase = () => {
    if (process.env.REACT_APP_API_URL) return `${process.env.REACT_APP_API_URL}/market`;
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return '/api/market'; // use proxy in development
    }
    return `http://${hostname}:5001/api/market`; // direct connection for network access
  };

  // Constants
  const API_BASE = getApiBase();
  const MONTH_NAMES = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  const COLLECTION_LABELS = {
    nifty50: 'NIFTY 50',
    fo: 'F&O',
    cash: 'CASH',
    cas: 'CAS'
  };

  const COLLECTION_TAG_CLASS = {
    nifty50: 'n50',
    fo: 'fo',
    cash: 'oth',
    cas: 'cas'
  };

  // ═══ HELPER FUNCTIONS ═══
  const showToast = (message, type = 'info', duration = 3500) => {
    setToast({ show: true, message, type });
    setTimeout(() => setToast({ show: false, message: '', type: 'info' }), duration);
  };

  const formatNumber = (n) => {
    if (n === null || n === undefined || n === '—') return '—';
    const num = parseFloat(n);
    if (isNaN(num)) return '—';
    return num.toLocaleString('en-IN', {
      maximumFractionDigits: 0,
      minimumFractionDigits: 0
    });
  };

  const fmtCsVal = (n) => {
    const num = parseFloat(n);
    if (isNaN(num)) return '—';
    const sign = num >= 0 ? '+' : '';
    return sign + formatNumber(num);
  };

  const getYear = (d) => new Date(d).getUTCFullYear();
  const getMonth = (d) => new Date(d).getUTCMonth() + 1;
  const getDay = (d) => new Date(d).getUTCDate();

  const formatDisplayDate = (isoStr) => {
    if (!isoStr) return '—';
    const d = new Date(isoStr);
    const dd = String(d.getUTCDate()).padStart(2, '0');
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const yyyy = d.getUTCFullYear();
    return `${dd}-${mm}-${yyyy}`;
  };

  // ═══ INITIALISE: FETCH ALL DATES ═══
  useEffect(() => {
    const controller = new AbortController();

    const initialise = async () => {
      setLoading(true);
      try {
        const res = await authFetch(`${API_BASE}/dates`, { signal: controller.signal });
        const json = await res.json();

        const dates = (json.allDates || [])
          .map(d => new Date(d))
          .filter(d => !isNaN(d));

        setAllDates(dates);

        // Unique years — newest first
        const years = [...new Set(dates.map(d => getYear(d)))]
          .sort((a, b) => b - a);
        setAvailableYears(years);

        // Auto-select newest year
        if (years.length > 0) {
          setSelectedYear(years[0].toString());
        }
      } catch (err) {
        if (err.name === 'AbortError') return; // Cancelled by cleanup — ignore
        console.error('Init error:', err);
        showToast('Failed to load dates from server.', 'error', 6000);
      } finally {
        setLoading(false);
      }
    };

    initialise();
    return () => controller.abort(); // Cancel fetch on unmount / StrictMode cleanup
  }, []);

  // ═══ POPULATE MONTHS WHEN YEAR CHANGES ═══
  useEffect(() => {
    if (!selectedYear || allDates.length === 0) return;

    const yr = parseInt(selectedYear);
    const months = [...new Set(
      allDates
        .filter(d => getYear(d) === yr)
        .map(d => getMonth(d))
    )].sort((a, b) => b - a);

    setAvailableMonths(months);
    setSelectedMonth('');
    setSelectedDate('');
    clearDisplay();

    // Auto-select newest month
    if (months.length > 0) {
      setSelectedMonth(months[0].toString());
    }
  }, [selectedYear, allDates]);

  // ═══ POPULATE DATES WHEN MONTH CHANGES ═══
  useEffect(() => {
    if (!selectedYear || !selectedMonth || allDates.length === 0) return;

    const yr = parseInt(selectedYear);
    const mo = parseInt(selectedMonth);

    const filtered = allDates.filter(d =>
      getYear(d) === yr && getMonth(d) === mo
    );

    // Sort newest first, deduplicate by day
    const seen = new Set();
    const uniqueDays = [];
    filtered
      .sort((a, b) => b - a)
      .forEach(d => {
        const day = getDay(d);
        if (!seen.has(day)) {
          seen.add(day);
          uniqueDays.push(d);
        }
      });

    setAvailableDates(uniqueDays);
    setSelectedDate('');
    clearDisplay();

    // Auto-select newest date
    if (uniqueDays.length > 0) {
      setSelectedDate(uniqueDays[0].toISOString());
    }
  }, [selectedMonth, selectedYear, allDates]);

  // ═══ FETCH DATA WHEN DATE OR COLLECTION CHANGES ═══
  useEffect(() => {
    if (selectedDate) {
      fetchAllData();
    }
  }, [selectedDate, selectedCollection]);

  // ═══ DAILY HIGHLIGHTS: AUTO-LOAD ONCE PER DAY (WHEN ENABLED) ═══
  useEffect(() => {
    if (autoHighlightRef.current) return;
    autoHighlightRef.current = true;

    let lastShown = '';
    try {
      lastShown = window.localStorage.getItem(HIGHLIGHT_LAST_SHOWN_KEY) || '';
    } catch {
      // Storage can be unavailable in private browsing modes.
    }

    if (lastShown !== getTodayKey()) {
      setHighlightToasts([]);
      loadDailyHighlights();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ═══ KEYBOARD SHORTCUT: Ctrl+Shift+H → RELOAD HIGHLIGHTS ═══
  useEffect(() => {
    const onKey = (e) => {
      if (e.ctrlKey && e.shiftKey && e.key === 'H') {
        setHighlightToasts([]);
        setTimeout(() => loadDailyHighlights(), 100);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ═══ FETCH ALL DATA ═══
  const fetchAllData = async () => {
    await Promise.all([
      fetchAduSummary(),
      fetchTableData(),
      fetchChartData()
    ]);
  };

  // ═══ REFRESH HANDLER ═══
  const handleRefresh = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await authFetch(`${API_BASE}/dates`);
      const json = await res.json();
      const dates = (json.allDates || [])
        .map(d => new Date(d))
        .filter(d => !isNaN(d));
      setAllDates(dates);

      if (selectedDate) {
        await Promise.all([
          fetchAduSummary(),
          fetchTableData(),
          fetchChartData(),
        ]);
        showToast('Data refreshed.', 'success', 1800);
      }
    } catch (err) {
      console.error('Refresh error:', err);
      showToast('Refresh failed.', 'error');
    } finally {
      setLoading(false);
    }
  };

  // ═══ FETCH ADU SUMMARY ═══
  const fetchAduSummary = async () => {
    if (!selectedDate) return;
    try {
      const dateParam = selectedDate.split('T')[0];
      const url = `${API_BASE}/${selectedCollection}/adu-summary?date=${dateParam}`;
      const res = await authFetch(url);
      const json = await res.json();

      const s = json.summary;
      if (s) {
        const netVal = s.netCsValue !== undefined ? s.netCsValue
          : (s.advance.totalCsValue + s.decline.totalCsValue + s.unchanged.totalCsValue);
        const totalStocks = s.totalStocks !== undefined ? s.totalStocks
          : (s.advance.count + s.decline.count + s.unchanged.count);

        setSummaryData({
          advance: s.advance,
          decline: s.decline,
          unchanged: s.unchanged,
          netCsValue: netVal,
          totalStocks: totalStocks
        });
      }
    } catch (err) {
      console.error('ADU fetch error:', err);
      showToast('Error fetching ADU summary.', 'error');
    }
  };

  // ═══ FETCH TABLE DATA ═══
  const fetchTableData = async () => {
    if (!selectedDate) return;
    try {
      const dateObj = new Date(selectedDate);
      const startDay = new Date(dateObj);
      startDay.setUTCHours(0, 0, 0, 0);
      const endDay = new Date(dateObj);
      endDay.setUTCHours(23, 59, 59, 999);

      const url = `${API_BASE}/${selectedCollection}?startDate=${startDay.toISOString()}&endDate=${endDay.toISOString()}&limit=5000&sortBy=csValue&sortOrder=desc`;
      const res = await authFetch(url);
      const json = await res.json();

      const records = (json.data || []).map(r => ({
        symbol: r.symbol,
        csValue: r.csValue?.$numberDecimal
          ? parseFloat(r.csValue.$numberDecimal)
          : parseFloat(r.csValue || 0)
      }));

      // Bullish: csValue > 100, sorted desc
      const bullish = records
        .filter(r => r.csValue > 100)
        .sort((a, b) => b.csValue - a.csValue);

      // Bearish: csValue < -10, sorted asc
      const bearish = records
        .filter(r => r.csValue < -10)
        .sort((a, b) => a.csValue - b.csValue);

      setBullishStocks(bullish);
      setBearishStocks(bearish);
    } catch (err) {
      console.error('Table fetch error:', err);
      showToast('Error fetching stock data.', 'error');
    }
  };

  // ═══ FETCH CHART DATA ═══
  const fetchChartData = async () => {
    if (!selectedYear || !selectedMonth) return;

    setChartLoading(true);
    try {
      const yr = parseInt(selectedYear);
      const mo = parseInt(selectedMonth);

      const startDate = new Date(Date.UTC(yr, mo - 1, 1, 0, 0, 0, 0));
      const endDate = new Date(Date.UTC(yr, mo, 0, 23, 59, 59, 999));

      const url = `${API_BASE}/${selectedCollection}/chart-data?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
      const res = await authFetch(url);
      const json = await res.json();

      const monthData = (json.chartData || [])
        .sort((a, b) => new Date(a.date) - new Date(b.date));

      renderChart(monthData);
    } catch (err) {
      console.error('Chart fetch error:', err);
      showToast('Error fetching chart data.', 'error');
    } finally {
      setChartLoading(false);
    }
  };

  // ═══ RENDER CHART ═══
  const renderChart = (data) => {
    const labels = data.map(d => {
      const dt = new Date(d.date);
      return `${String(getDay(dt)).padStart(2, '0')} ${MONTH_NAMES[getMonth(dt) - 1].slice(0, 3)}`;
    });

    const values = data.map(d => Math.round(d.totalCsValue));

    // Per-point colors
    const PT_GREEN = '#00f260';
    const PT_RED = '#ff6b6b';
    const pointBg = values.map(v => v >= 0 ? PT_GREEN : PT_RED);
    const pointBdr = values.map(v => v >= 0 ? 'rgba(0,242,96,0.45)' : 'rgba(255,107,107,0.45)');

    // Create gradient for fill
    const createGradient = (ctx, chartArea) => {
      const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      gradient.addColorStop(0, 'rgba(0,242,96,0.22)');
      gradient.addColorStop(0.5, 'rgba(0,242,96,0.06)');
      gradient.addColorStop(0.5, 'rgba(255,107,107,0.06)');
      gradient.addColorStop(1, 'rgba(255,107,107,0.22)');
      return gradient;
    };

    setChartData({
      labels,
      datasets: [{
        label: `CS Value (${COLLECTION_LABELS[selectedCollection]})`,
        data: values,
        borderColor: (context) => {
          const value = context.parsed?.y;
          return value >= 0 ? PT_GREEN : PT_RED;
        },
        segment: {
          borderColor: ctx => ctx.p1.parsed.y >= 0 ? PT_GREEN : PT_RED,
        },
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) return 'rgba(0,242,96,0.1)';
          return createGradient(ctx, chartArea);
        },
        borderWidth: 2.5,
        pointRadius: values.length <= 25 ? 5 : 3,
        pointHoverRadius: 8,
        pointBackgroundColor: pointBg,
        pointBorderColor: pointBdr,
        pointBorderWidth: 1.5,
        pointHoverBackgroundColor: pointBg,
        pointHoverBorderColor: '#ffffff',
        pointHoverBorderWidth: 2,
        fill: 'origin',
        tension: 0.35,
        spanGaps: true
      }]
    });
  };

  // ═══ CLEAR DISPLAY ═══
  const clearDisplay = () => {
    setSummaryData({
      advance: { count: 0, totalCsValue: 0 },
      decline: { count: 0, totalCsValue: 0 },
      unchanged: { count: 0, totalCsValue: 0 },
      netCsValue: 0,
      totalStocks: 0
    });
    setBullishStocks([]);
    setBearishStocks([]);
    setChartData(null);
  };

  // ═══ HIGHLIGHT TOASTS ═══
  const getHighlightsBase = () => {
    if (process.env.REACT_APP_API_URL) return `${process.env.REACT_APP_API_URL}/highlights`;
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') return '/api/highlights';
    return `http://${hostname}:5001/api/highlights`;
  };

  const closeHighlightToast = (id) => {
    setHighlightToasts(prev => prev.map(t => t.id === id ? { ...t, hiding: true } : t));
    setTimeout(() => {
      setHighlightToasts(prev => prev.filter(t => t.id !== id));
    }, 400);
  };

  const handleShowDailyHighlights = async () => {
    setHighlightToasts([]);
    await loadDailyHighlights();
  };

  const loadDailyHighlights = async () => {
    if (highlightRequestRef.current) return;
    highlightRequestRef.current = true;
    setHighlightsLoading(true);

    try {
      const today = getTodayKey();
      const res = await authFetch(`${getHighlightsBase()}?date=${today}`);
      if (!res.ok) return;
      const data = await res.json();

      const fn = (v) => {
        const n = Number(v);
        if (isNaN(n) || n === 0) return '0';
        return n.toLocaleString('en-IN', { maximumFractionDigits: 0 });
      };

      const toasts = [];
      let delay = 800;
      const interval = 300;

      // ── TOAST 1: Bhadra & Swing Reversal ──
      const bhadra = data.bhadraInfo;
      const swing = data.swingReversal;
      let t1Body = '';

      if (bhadra && bhadra.isBhadra) {
        t1Body += `
          <div class="ht-info-row ht-clickable" style="cursor:pointer" onclick="window.open('/bhadra','_blank')">
            <span class="ht-info-icon">⚠️</span>
            <div class="ht-info-content">
              <div class="ht-info-label">Time Trend Shift</div>
              <div class="ht-info-value">Active Today</div>
              <div class="ht-info-details">
                ${(() => {
                  if (!bhadra.periods || bhadra.periods.length === 0) return '<span class="ht-status">Active Period</span>';
                  const p = bhadra.periods[0];
                  const startTime = typeof p.start === 'string' ? p.start.split(' at ')[1] || p.start : 'N/A';
                  const endTime   = typeof p.end   === 'string' ? p.end.split(' at ')[1]   || p.end   : 'N/A';
                  return `<span class="ht-status">${startTime} → ${endTime}</span>`;
                })()}
              </div>
            </div>
          </div>`;
      } else {
        t1Body += `
          <div class="ht-info-row ht-clickable" style="cursor:pointer" onclick="window.open('/bhadra','_blank')">
            <span class="ht-info-icon">✅</span>
            <div class="ht-info-content">
              <div class="ht-info-label">Time Trend Shift</div>
              <div class="ht-info-value">Clear for Trading</div>
              <div class="ht-info-details"><span class="ht-status inactive">No Time Trend Shift Today</span></div>
            </div>
          </div>`;
      }

      if (swing && swing.isReversalDate) {
        t1Body += `
          <div class="ht-info-row ht-clickable" style="cursor:pointer" onclick="window.open('/gann-reversal','_blank')">
            <span class="ht-info-icon">🔄</span>
            <div class="ht-info-content">
              <div class="ht-info-label">Gann Reversal</div>
              <div class="ht-info-value">Reversal Date</div>
              <div class="ht-info-details">Sun Degree: <strong>${swing.sunDegree || 'N/A'}°</strong>${swing.reversalType ? `<br>${swing.reversalType}` : ''}</div>
            </div>
          </div>`;
      } else {
        t1Body += `
          <div class="ht-info-row ht-clickable" style="cursor:pointer" onclick="window.open('/gann-reversal','_blank')">
            <span class="ht-info-icon">📍</span>
            <div class="ht-info-content">
              <div class="ht-info-label">Gann Reversal</div>
              <div class="ht-info-value">No Reversal Today</div>
              <div class="ht-info-details">Sun Degree: <strong>${swing?.sunDegree || 'N/A'}°</strong></div>
            </div>
          </div>`;
      }

      toasts.push({ id: 'ht-bhadra-swing', icon: '🌟', title: 'Daily Status', subtitle: 'Time Trend Shift & Gann Reversal', bodyHtml: t1Body, show: false, hiding: false, delay });
      delay += interval;

      // ── TOAST 2: Nakshatra ──
      const nakshatra = data.nakshatraHighlight;
      if (nakshatra && nakshatra.nakshatra) {
        let t2Body = `
          <div class="ht-info-row">
            <span class="ht-info-icon">⭐</span>
            <div class="ht-info-content">
              <div class="ht-info-label">Today's Nakshatra</div>
              <div class="ht-info-value">${nakshatra.nakshatra}</div>
            </div>
          </div>`;

        if (nakshatra.topBuying && nakshatra.topBuying.length > 0) {
          t2Body += `
            <div class="ht-info-row">
              <span class="ht-info-icon">📈</span>
              <div class="ht-info-content">
                <div class="ht-info-label">Top Buying (₹${fn(nakshatra.totalBuying || 0)} L)</div>
                <div class="ht-stocks-grid">
                  ${nakshatra.topBuying.slice(0, 6).map(s => `<span class="ht-chip buy">${s.symbol}</span>`).join('')}
                </div>
              </div>
            </div>`;
        }

        if (nakshatra.topSelling && nakshatra.topSelling.length > 0) {
          t2Body += `
            <div class="ht-info-row">
              <span class="ht-info-icon">📉</span>
              <div class="ht-info-content">
                <div class="ht-info-label">Top Selling (₹${fn(Math.abs(nakshatra.totalSelling || 0))} L)</div>
                <div class="ht-stocks-grid">
                  ${nakshatra.topSelling.slice(0, 6).map(s => `<span class="ht-chip sell">${s.symbol}</span>`).join('')}
                </div>
              </div>
            </div>`;
        }

        toasts.push({ id: 'ht-nakshatra', icon: '⭐', title: nakshatra.nakshatra, subtitle: "Today's Nakshatra", bodyHtml: t2Body, show: false, hiding: false, delay });
        delay += interval;
      }

      // ── TOAST 3: Monthly Highlights ──
      const monthly = data.monthlyHighlights;
      if (monthly && monthly.highestBuying && monthly.highestSelling) {
        let buyStocks = [], sellStocks = [];
        ['N50', 'FO', 'Others'].forEach(cat => {
          (monthly.highestBuying[cat] || []).forEach(e => { if (e.date === today) buyStocks.push({ ...e, category: cat }); });
          (monthly.highestSelling[cat] || []).forEach(e => { if (e.date === today) sellStocks.push({ ...e, category: cat }); });
        });
        buyStocks.sort((a, b) => b.csValue - a.csValue);
        sellStocks.sort((a, b) => a.csValue - b.csValue);

        if (buyStocks.length > 0 || sellStocks.length > 0) {
          let t3Body = '';
          if (buyStocks.length > 0) {
            const total = buyStocks.reduce((s, x) => s + x.csValue, 0);
            t3Body += `
              <div class="ht-info-row">
                <span class="ht-info-icon">💹</span>
                <div class="ht-info-content">
                  <div class="ht-info-label">Month's Highest Buying Today</div>
                  <div class="ht-info-value">₹${fn(total)} L</div>
                  <div class="ht-stocks-grid">
                    ${buyStocks.slice(0, 10).map(s => `<span class="ht-chip buy">${s.symbol}</span>`).join('')}
                  </div>
                </div>
              </div>`;
          }
          if (sellStocks.length > 0) {
            const total = sellStocks.reduce((s, x) => s + Math.abs(x.csValue), 0);
            t3Body += `
              <div class="ht-info-row">
                <span class="ht-info-icon">📉</span>
                <div class="ht-info-content">
                  <div class="ht-info-label">Month's Highest Selling Today</div>
                  <div class="ht-info-value">₹${fn(total)} L</div>
                  <div class="ht-stocks-grid">
                    ${sellStocks.slice(0, 10).map(s => `<span class="ht-chip sell">${s.symbol}</span>`).join('')}
                  </div>
                </div>
              </div>`;
          }
          toasts.push({ id: 'ht-monthly', icon: '📊', title: "Month's Peak Activity", subtitle: 'Highest Activity Today', bodyHtml: t3Body, show: false, hiding: false, delay });
        }
      }

      // ── TOAST 4: Top Sectors ──
      const sectors = data.topSectors;
      if (sectors && (sectors.topBuying?.length > 0 || sectors.topSelling?.length > 0)) {
        const topBuy = (sectors.topBuying || []).slice(0, 2);
        const topSell = (sectors.topSelling || []).slice(0, 2);
        let t4Body = '';

        if (topBuy.length > 0) {
          t4Body += `
            <div class="ht-info-row">
              <span class="ht-info-icon">🏭</span>
              <div class="ht-info-content">
                <div class="ht-info-label">Top Bullish Sectors</div>
                <div class="ht-stocks-grid">
                  ${topBuy.map(s => `
                    <span class="ht-chip buy" title="₹${fn(s.totalCs)} L | ${s.stockCount} stocks">
                      ${s.sector}
                      <span style="opacity:0.75;font-size:0.7em;margin-left:3px">₹${fn(s.totalCs)}L</span>
                    </span>`).join('')}
                </div>
              </div>
            </div>`;
        }

        if (topSell.length > 0) {
          t4Body += `
            <div class="ht-info-row">
              <span class="ht-info-icon">📉</span>
              <div class="ht-info-content">
                <div class="ht-info-label">Top Bearish Sectors</div>
                <div class="ht-stocks-grid">
                  ${topSell.map(s => `
                    <span class="ht-chip sell" title="₹${fn(Math.abs(s.totalCs))} L | ${s.stockCount} stocks">
                      ${s.sector}
                      <span style="opacity:0.75;font-size:0.7em;margin-left:3px">₹${fn(Math.abs(s.totalCs))}L</span>
                    </span>`).join('')}
                </div>
              </div>
            </div>`;
        }

        toasts.push({ id: 'ht-sectors', icon: '🏭', title: "Today's Sectors", subtitle: 'Top Bullish & Bearish', bodyHtml: t4Body, show: false, hiding: false, delay });
        delay += interval;
      }

      // ── TOAST 5: Daily Spike Stocks ──
      try {
        const mktBase = (() => {
          if (process.env.REACT_APP_API_URL) return `${process.env.REACT_APP_API_URL}/market`;
          const h = window.location.hostname;
          return (h === 'localhost' || h === '127.0.0.1') ? '/api/market' : `http://${h}:5001/api/market`;
        })();
        const now = new Date();
        const yr  = now.getUTCFullYear();
        const mo  = now.getUTCMonth() + 1;
        const dy  = now.getUTCDate();
        const todayStart = new Date(Date.UTC(yr, mo - 1, dy, 0, 0, 0, 0)).toISOString();
        const todayEnd   = new Date(Date.UTC(yr, mo - 1, dy, 23, 59, 59, 999)).toISOString();

        // Approximate trading days from start of month to today (no holiday calendar)
        let approxTradingDays = 0;
        for (let d = 1; d <= dy; d++) {
          const wd = new Date(yr, mo - 1, d).getDay();
          if (wd !== 0 && wd !== 6) approxTradingDays++;
        }
        approxTradingDays = Math.max(approxTradingDays, 1);

        const SPIKE_MULT = 1.5;
        const MIN_ABS    = 5; // ignore noise below ₹5L
        const allSpikeBuying  = [];
        const allSpikeSelling = [];

        await Promise.all(['nifty50', 'fo', 'cash'].map(async (col) => {
          try {
            const [symRes, dayRes] = await Promise.all([
              authFetch(`${mktBase}/symbols/${col}?year=${yr}&month=${mo}`),
              authFetch(`${mktBase}/${col}?startDate=${todayStart}&endDate=${todayEnd}&limit=5000`),
            ]);
            const [symJson, dayJson] = await Promise.all([symRes.json(), dayRes.json()]);

            const monthlyMap = {};
            (symJson.symbolsData || []).forEach(s => { monthlyMap[s.symbol] = s.totalCsValue; });

            const todayAgg = {};
            (dayJson.data || []).forEach(r => {
              const sym = r.symbol;
              const val = r.csValue?.$numberDecimal
                ? parseFloat(r.csValue.$numberDecimal)
                : parseFloat(r.csValue || 0);
              todayAgg[sym] = (todayAgg[sym] || 0) + val;
            });

            Object.entries(todayAgg).forEach(([sym, todayVal]) => {
              if (todayVal === 0) return;
              const monthlyVal = monthlyMap[sym] ?? 0;
              const monthlyAvg = monthlyVal / approxTradingDays;

              if (todayVal > MIN_ABS) {
                const isReversal = monthlyAvg <= 0;
                const ratio = isReversal ? 999 : todayVal / Math.max(monthlyAvg, 0.001);
                if (isReversal || ratio >= SPIKE_MULT) {
                  allSpikeBuying.push({ symbol: sym, csValue: todayVal, ratio: Math.min(ratio, 99), isReversal, col });
                }
              } else if (todayVal < -MIN_ABS) {
                const isReversal = monthlyAvg >= 0;
                const ratio = isReversal ? 999 : Math.abs(todayVal) / Math.max(Math.abs(monthlyAvg), 0.001);
                if (isReversal || ratio >= SPIKE_MULT) {
                  allSpikeSelling.push({ symbol: sym, csValue: todayVal, ratio: Math.min(ratio, 99), isReversal, col });
                }
              }
            });
          } catch (colErr) {
            console.error(`Spike fetch error (${col}):`, colErr);
          }
        }));

        allSpikeBuying.sort((a, b)  => b.ratio - a.ratio);
        allSpikeSelling.sort((a, b) => b.ratio - a.ratio);
        const topSpikeBuy  = allSpikeBuying.slice(0, 8);
        const topSpikeSell = allSpikeSelling.slice(0, 8);

        if (topSpikeBuy.length > 0 || topSpikeSell.length > 0) {
          let t5Body = '';

          if (topSpikeBuy.length > 0) {
            t5Body += `
              <div class="ht-info-row">
                <span class="ht-info-icon">⚡</span>
                <div class="ht-info-content">
                  <div class="ht-info-label">Surge Buying (≥1.5× monthly avg)</div>
                  <div class="ht-stocks-grid">
                    ${topSpikeBuy.map(s => `
                      <span class="ht-chip"
                        style="background:${s.isReversal ? 'rgba(167,243,208,0.12)' : 'rgba(245,158,11,0.18)'};border:1px solid ${s.isReversal ? 'rgba(52,211,153,0.5)' : 'rgba(245,158,11,0.5)'};color:${s.isReversal ? '#6ee7b7' : '#fcd34d'}"
                        title="₹${fn(s.csValue)} L · ${s.isReversal ? 'Reversal ↺' : s.ratio.toFixed(1) + '× avg'} · ${s.col === 'nifty50' ? 'N50' : 'F&O'}">
                        ${s.symbol}<span style="opacity:0.7;font-size:0.6em;margin-left:2px">${s.isReversal ? '↺' : s.ratio.toFixed(1) + '×'}</span>
                      </span>`).join('')}
                  </div>
                </div>
              </div>`;
          }

          if (topSpikeSell.length > 0) {
            t5Body += `
              <div class="ht-info-row">
                <span class="ht-info-icon">⚡</span>
                <div class="ht-info-content">
                  <div class="ht-info-label">Surge Selling (≥1.5× monthly avg)</div>
                  <div class="ht-stocks-grid">
                    ${topSpikeSell.map(s => `
                      <span class="ht-chip"
                        style="background:${s.isReversal ? 'rgba(251,113,133,0.12)' : 'rgba(251,146,60,0.18)'};border:1px solid ${s.isReversal ? 'rgba(251,113,133,0.5)' : 'rgba(251,146,60,0.5)'};color:${s.isReversal ? '#fca5a5' : '#fb923c'}"
                        title="₹${fn(Math.abs(s.csValue))} L · ${s.isReversal ? 'Reversal ↺' : s.ratio.toFixed(1) + '× avg'} · ${s.col === 'nifty50' ? 'N50' : 'F&O'}">
                        ${s.symbol}<span style="opacity:0.7;font-size:0.6em;margin-left:2px">${s.isReversal ? '↺' : s.ratio.toFixed(1) + '×'}</span>
                      </span>`).join('')}
                  </div>
                </div>
              </div>`;
          }

          toasts.push({ id: 'ht-spikes', icon: '⚡', title: 'Daily Spike Stocks', subtitle: 'Surge vs Monthly Avg', bodyHtml: t5Body, show: false, hiding: false, delay });
          delay += interval;
        }
      } catch (spikeErr) {
        console.error('Spike toast error:', spikeErr);
      }

      setHighlightToasts(toasts);
      toasts.forEach(t => {
        setTimeout(() => {
          setHighlightToasts(prev => prev.map(x => x.id === t.id ? { ...x, show: true } : x));
        }, t.delay);
      });

      try {
        window.localStorage.setItem(HIGHLIGHT_LAST_SHOWN_KEY, today);
      } catch {
        // The request succeeded; only persistence is unavailable.
      }
    } catch (err) {
      console.error('Error loading highlights:', err);
    } finally {
      highlightRequestRef.current = false;
      setHighlightsLoading(false);
    }
  };

  // ═══ CHART OPTIONS ═══
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 600, easing: 'easeInOutQuart' },
    interaction: { mode: 'index', intersect: false },
    plugins: {
      legend: { display: false },
      datalabels: {
        display: true,
        color: 'rgba(180,180,180,0.75)',
        font: { size: 9, weight: '500' },
        formatter: (value) => {
          if (Math.abs(value) >= 1000) {
            return (value >= 0 ? '' : '-') + Math.abs(value / 1000).toFixed(1) + 'k';
          }
          return value;
        },
        anchor: (context) => context.dataset.data[context.dataIndex] >= 0 ? 'end' : 'start',
        align: (context) => context.dataset.data[context.dataIndex] >= 0 ? 'top' : 'bottom',
        offset: 4,
        clamp: true
      },
      tooltip: {
        backgroundColor: 'rgba(12,12,26,0.97)',
        borderWidth: 1,
        borderColor: 'rgba(255,255,255,0.2)',
        titleFont: { size: 11, weight: '700' },
        bodyFont: { size: 11 },
        padding: 10,
        callbacks: {
          title: (items) => `📅 ${items[0].label}`,
          label: (item) => {
            const v = item.raw;
            const sign = v >= 0 ? '▲ ' : '▼ ';
            return ` ${sign}CS Value: ${formatNumber(v)}`;
          },
          labelColor: (item) => ({
            backgroundColor: item.raw >= 0 ? '#00f260' : '#ff6b6b',
            borderColor: item.raw >= 0 ? '#00f260' : '#ff6b6b',
            borderRadius: 3
          })
        }
      }
    },
    scales: {
      x: {
        grid: {
          color: 'rgba(255,255,255,0.04)',
          drawBorder: false
        },
        ticks: {
          color: 'rgba(255,255,255,0.5)',
          font: { size: 10 },
          maxRotation: 45,
          autoSkip: true,
          maxTicksLimit: 16
        }
      },
      y: {
        grid: {
          color: (ctx) => ctx.tick.value === 0
            ? 'rgba(255,255,255,0.28)'
            : 'rgba(255,255,255,0.05)',
          lineWidth: (ctx) => ctx.tick.value === 0 ? 1.5 : 1,
          drawBorder: false
        },
        ticks: {
          color: (ctx) => ctx.tick.value === 0
            ? 'rgba(255,255,255,0.85)'
            : 'rgba(255,255,255,0.45)',
          font: { size: 10 },
          callback: (v) => {
            if (Math.abs(v) >= 1000) {
              return (v >= 0 ? '' : '-') + Math.abs(v / 1000).toFixed(0) + 'k';
            }
            return v;
          },
          maxTicksLimit: 10,
        }
      }
    }
  };

  // ═══ RENDER ═══
  return (
    <AppLayout pageBadge="Market Insight">
    <div className={`market-overview-container${filterOpen ? ' filter-open' : ''}`}>
      {/* MOBILE/TABLET FILTER TOGGLE */}
      {!filterOpen && (
        <button
          type="button"
          className="filter-toggle-btn"
          onClick={() => setFilterOpen(true)}
          aria-label="Show filters"
        >
          <span>☰</span>
          <span>Filters</span>
        </button>
      )}

      {/* FILTER BAR */}
      <div className={`filter-bar fixed top-[73px] left-0 right-0 z-40${filterOpen ? ' is-open' : ''}`}>
        <div className="mi-filter-inner flex flex-wrap items-center gap-3">
          {/* Year Selector */}
          <div className="mi-filter-year flex items-center gap-2">
            <label className="text-xs text-white/70 font-semibold">Year:</label>
            <select 
              className="filter-select"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
            >
              <option value="">─ Year ─</option>
              {availableYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>

          {/* Month Selector */}
          <div className="mi-filter-month flex items-center gap-2">
            <label className="text-xs text-white/70 font-semibold">Month:</label>
            <select 
              className="filter-select"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              disabled={!selectedYear}
            >
              <option value="">─ Month ─</option>
              {availableMonths.map(month => (
                <option key={month} value={month}>
                  {MONTH_NAMES[month - 1]}
                </option>
              ))}
            </select>
          </div>

          {/* Date Selector */}
          <div className="mi-filter-date flex items-center gap-2">
            <label className="text-xs text-white/70 font-semibold">Date:</label>
            <input
              type="date"
              className="filter-select mi-date-input"
              value={selectedDate ? selectedDate.slice(0, 10) : ''}
              min={availableDates.length ? availableDates[availableDates.length - 1].toISOString().slice(0, 10) : ''}
              max={availableDates.length ? availableDates[0].toISOString().slice(0, 10) : ''}
              disabled={!selectedMonth}
              onChange={(e) => {
                const matchingDate = availableDates.find(
                  (date) => date.toISOString().slice(0, 10) === e.target.value,
                );

                if (matchingDate) {
                  setSelectedDate(matchingDate.toISOString());
                } else {
                  showToast('No market data available for this date.', 'error');
                }
              }}
            />
          </div>

          <div className="mi-filter-divider h-6 w-px bg-white/20 mx-2" />

          {/* Collection Toggles */}
          <div className="mi-filter-market flex items-center gap-2">
            <button 
              className={`col-btn ${selectedCollection === 'nifty50' ? 'active n50' : ''}`}
              onClick={() => setSelectedCollection('nifty50')}
              data-col="nifty50"
            >
              N50
            </button>
            <button 
              className={`col-btn ${selectedCollection === 'fo' ? 'active fo' : ''}`}
              onClick={() => setSelectedCollection('fo')}
              data-col="fo"
            >
              F&O
            </button>
            <button 
              className={`col-btn ${selectedCollection === 'cash' ? 'active oth' : ''}`}
              onClick={() => setSelectedCollection('cash')}
              data-col="cash"
            >
              CASH
            </button>
            <button 
              className={`col-btn ${selectedCollection === 'cas' ? 'active cas' : ''}`}
              onClick={() => setSelectedCollection('cas')}
              data-col="cas"
            >
              CAS
            </button>
          </div>

          <div className="mi-filter-divider h-6 w-px bg-white/20 mx-2" />

          {/* Refresh Button */}
          <button 
            className="refresh-btn mi-filter-refresh flex items-center gap-2"
            onClick={handleRefresh}
            disabled={!selectedDate || loading}
          >
            <span>{loading ? '⏳' : '🔄'}</span>
            <span>Refresh</span>
          </button>

          <button
            type="button"
            className="refresh-btn mi-filter-highlights flex items-center gap-2"
            onClick={handleShowDailyHighlights}
            disabled={highlightsLoading}
          >
            <span aria-hidden="true">★</span>
            <span>{highlightsLoading ? 'Loading...' : "Today's Highlights"}</span>
          </button>

          <button
            type="button"
            className="refresh-btn mi-filter-close"
            onClick={() => setFilterOpen(false)}
            aria-label="Close filters"
          >
            <span>✕</span>
            <span>Close Filter</span>
          </button>

          {/* Selected Date Display */}
          <div className="mi-filter-selected-date ml-auto bg-purple-500/20 px-4 py-2 rounded-lg border border-purple-500/30">
            <span className="text-sm font-semibold">
              {formatDisplayDate(selectedDate)}
            </span>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT */}
      <main className="flex-1 overflow-hidden px-6 py-4" style={{ paddingTop: '140px' }}>
        {/* SUMMARY CARDS */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
          {/* Advance Card */}
          <div className="summary-card advance">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-white/90">Advance</h3>
              <span className="text-lg">📈</span>
            </div>
            <div className="flex items-stretch gap-0 justify-center">
              <div className="flex-1 flex flex-col items-center justify-center py-1">
                <p className="text-xs text-white/60 mb-1">CS Value</p>
                <p className="text-3xl font-bold text-green-400 leading-none">{formatNumber(summaryData.advance.totalCsValue)}</p>
              </div>
              <div className="w-px bg-white/20 mx-2" />
              <div className="flex-1 flex flex-col items-center justify-center py-1">
                <p className="text-xs text-white/60 mb-1">Count</p>
                <p className="text-3xl font-bold text-green-400 leading-none">{summaryData.advance.count}</p>
              </div>
            </div>
          </div>

          {/* Decline Card */}
          <div className="summary-card decline">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-white/90">Decline</h3>
              <span className="text-lg">📉</span>
            </div>
            <div className="flex items-stretch gap-0 justify-center">
              <div className="flex-1 flex flex-col items-center justify-center py-1">
                <p className="text-xs text-white/60 mb-1">CS Value</p>
                <p className="text-3xl font-bold text-red-400 leading-none">{formatNumber(summaryData.decline.totalCsValue)}</p>
              </div>
              <div className="w-px bg-white/20 mx-2" />
              <div className="flex-1 flex flex-col items-center justify-center py-1">
                <p className="text-xs text-white/60 mb-1">Count</p>
                <p className="text-3xl font-bold text-red-400 leading-none">{summaryData.decline.count}</p>
              </div>
            </div>
          </div>

          {/* Unchanged Card */}
          <div className="summary-card unchanged">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-white/90">Unchanged</h3>
              <span className="text-lg">➖</span>
            </div>
            <div className="flex items-stretch gap-0 justify-center">
              <div className="flex-1 flex flex-col items-center justify-center py-1">
                <p className="text-xs text-white/60 mb-1">CS Value</p>
                <p className="text-3xl font-bold text-blue-400 leading-none">{formatNumber(summaryData.unchanged.totalCsValue)}</p>
              </div>
              <div className="w-px bg-white/20 mx-2" />
              <div className="flex-1 flex flex-col items-center justify-center py-1">
                <p className="text-xs text-white/60 mb-1">Count</p>
                <p className="text-3xl font-bold text-blue-400 leading-none">{summaryData.unchanged.count}</p>
              </div>
            </div>
          </div>

          {/* Total/Net Card */}
          <div className="summary-card total">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-sm font-bold text-white/90">Net / Total</h3>
              <span className="text-lg">📊</span>
            </div>
            <div className="flex items-stretch gap-0 justify-center">
              <div className="flex-1 flex flex-col items-center justify-center py-1">
                <p className="text-xs text-white/60 mb-1">Net CS Value</p>
                <p className={`text-3xl font-bold leading-none ${parseFloat(summaryData.netCsValue) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                  {formatNumber(summaryData.netCsValue)}
                </p>
              </div>
              <div className="w-px bg-white/20 mx-2" />
              <div className="flex-1 flex flex-col items-center justify-center py-1">
                <p className="text-xs text-white/60 mb-1">Total</p>
                <p className="text-3xl font-bold text-yellow-400 leading-none">{summaryData.totalStocks}</p>
              </div>
            </div>
          </div>
        </div>

        {/* BOTTOM SECTION: TABLES + CHART */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4" style={{ height: 'calc(100vh - 310px)' }}>
          {/* BULLISH STOCKS TABLE */}
          <div className="table-card bullish lg:col-span-1" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="p-2 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-white/90 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-green-400" />
                  Bullish Stocks
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    onClick={() => copySymbols(bullishStocks, 'bullish')}
                    disabled={!bullishStocks.length}
                    title="Copy all bullish symbols for TradingView"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      fontSize: '0.68rem', fontWeight: 600,
                      padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                      border: copiedTable === 'bullish' ? '1px solid #10b981' : '1px solid rgba(16,185,129,0.4)',
                      background: copiedTable === 'bullish' ? 'rgba(16,185,129,0.25)' : 'rgba(16,185,129,0.1)',
                      color: copiedTable === 'bullish' ? '#6ee7b7' : '#10b981',
                      transition: 'all 0.2s',
                      opacity: bullishStocks.length ? 1 : 0.4,
                    }}
                  >
                    {copiedTable === 'bullish' ? '✓ Copied!' : '⧉ Copy'}
                  </button>
                  <span className="badge-green text-xs">{bullishStocks.length}</span>
                </div>
              </div>
            </div>
            <div className="table-scrollable">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>CS Value</th>
                  </tr>
                </thead>
                <tbody>
                  {bullishStocks.length > 0 ? (
                    bullishStocks.map((stock, idx) => (
                      <tr key={idx}>
                        <td>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {stock.symbol}
                            <a
                              href={`https://in.tradingview.com/chart/?symbol=NSE%3A${stock.symbol}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`View ${stock.symbol} on TradingView`}
                              style={{ color: '#60a5fa', lineHeight: 1, flexShrink: 0 }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                              </svg>
                            </a>
                            <a
                              href={calendarUrl(stock.symbol)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`View ${stock.symbol} CS Calendar`}
                              style={{ color: '#a78bfa', lineHeight: 1, flexShrink: 0 }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                              </svg>
                            </a>
                          </span>
                        </td>
                        <td className="csval-cell positive">{fmtCsVal(stock.csValue)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2">
                        <div className="empty-state">
                          <div className="empty-icon">📈</div>
                          <div>
                            {selectedDate ? 'No stocks match the filter' : 'Select a date to view data'}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* BEARISH STOCKS TABLE */}
          <div className="table-card bearish lg:col-span-1" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="p-2 border-b border-white/10">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-bold text-white/90 flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  Bearish Stocks
                </h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <button
                    onClick={() => copySymbols(bearishStocks, 'bearish')}
                    disabled={!bearishStocks.length}
                    title="Copy all bearish symbols for TradingView"
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      fontSize: '0.68rem', fontWeight: 600,
                      padding: '2px 8px', borderRadius: 6, cursor: 'pointer',
                      border: copiedTable === 'bearish' ? '1px solid #ef4444' : '1px solid rgba(239,68,68,0.4)',
                      background: copiedTable === 'bearish' ? 'rgba(239,68,68,0.25)' : 'rgba(239,68,68,0.1)',
                      color: copiedTable === 'bearish' ? '#fca5a5' : '#ef4444',
                      transition: 'all 0.2s',
                      opacity: bearishStocks.length ? 1 : 0.4,
                    }}
                  >
                    {copiedTable === 'bearish' ? '✓ Copied!' : '⧉ Copy'}
                  </button>
                  <span className="badge-red text-xs">{bearishStocks.length}</span>
                </div>
              </div>
            </div>
            <div className="table-scrollable">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Symbol</th>
                    <th>CS Value</th>
                  </tr>
                </thead>
                <tbody>
                  {bearishStocks.length > 0 ? (
                    bearishStocks.map((stock, idx) => (
                      <tr key={idx}>
                        <td>
                          <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                            {stock.symbol}
                            <a
                              href={`https://in.tradingview.com/chart/?symbol=NSE%3A${stock.symbol}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`View ${stock.symbol} on TradingView`}
                              style={{ color: '#60a5fa', lineHeight: 1, flexShrink: 0 }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
                              </svg>
                            </a>
                            <a
                              href={calendarUrl(stock.symbol)}
                              target="_blank"
                              rel="noopener noreferrer"
                              title={`View ${stock.symbol} CS Calendar`}
                              style={{ color: '#a78bfa', lineHeight: 1, flexShrink: 0 }}
                            >
                              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                                <line x1="3" y1="10" x2="21" y2="10" />
                              </svg>
                            </a>
                          </span>
                        </td>
                        <td className="csval-cell negative">{fmtCsVal(stock.csValue)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="2">
                        <div className="empty-state">
                          <div className="empty-icon">📉</div>
                          <div>
                            {selectedDate ? 'No stocks match the filter' : 'Select a date to view data'}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* CHART SECTION */}
          <div className="chart-card lg:col-span-3">
            <div className="mb-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-base font-bold text-white/90 flex items-center gap-2">
                  📊 <span>CS Value Trend</span>
                </h3>
                <span className={`chart-collection-tag ${COLLECTION_TAG_CLASS[selectedCollection]}`}>
                  {selectedCollection === 'nifty50' ? 'N50' : selectedCollection === 'fo' ? 'FO' : selectedCollection === 'cas' ? 'CAS' : 'OTH'}
                </span>
              </div>
              <p className="text-xs text-white/50">
                {selectedMonth && selectedYear ? `${MONTH_NAMES[parseInt(selectedMonth) - 1]} ${selectedYear}` : 'Select a date'}
              </p>
            </div>
            <div className="relative" style={{ flex: 1, minHeight: 0 }}>
              {chartLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="spinner" />
                </div>
              ) : chartData ? (
                <Line ref={chartRef} data={chartData} options={chartOptions} />
              ) : (
                <div className="flex items-center justify-center h-full text-white/50 text-sm">
                  Select a date to view chart
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* TOAST NOTIFICATION */}
      <div className={`toast ${toast.show ? 'show' : ''} ${toast.type}`}>
        {toast.message}
      </div>

      {/* HIGHLIGHT TOASTS CONTAINER */}
      <div className="ht-container">
        {highlightToasts.map(t => (
          <div
            key={t.id}
            className={`ht-toast${t.show ? ' show' : ''}${t.hiding ? ' hiding' : ''} ht-clickable`}
            onClick={() => {
              if (t.id === 'ht-nakshatra') {
                window.open('/nakshatra', '_blank');
              } else if (t.id === 'ht-monthly') {
                window.open('/cs-calendar', '_blank');
              } else if (t.id === 'ht-sectors') {
                window.open('/sector-radar', '_blank');
              } else if (t.id === 'ht-spikes') {
                window.open('/cs-calendar', '_blank');
              }
            }}
            style={{ cursor: 'pointer' }}
          >
            <div className="ht-header">
              <div className="ht-title">
                <span className="ht-icon">{t.icon}</span>
                <div className="ht-text">
                  <div className="ht-label">{t.subtitle}</div>
                  <div className="ht-heading">{t.title}</div>
                </div>
              </div>
              <button className="ht-close" onClick={(e) => { e.stopPropagation(); closeHighlightToast(t.id); }} title="Close">✕</button>
            </div>
            <div className="ht-body" dangerouslySetInnerHTML={{ __html: t.bodyHtml }} />
          </div>
        ))}
      </div>

      {/* PAGE LOADING OVERLAY */}
      {loading && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center">
          <div className="text-center">
            <div className="spinner mx-auto mb-4" />
            <p className="text-white font-semibold">Loading data...</p>
          </div>
        </div>
      )}
    </div>
    </AppLayout>
  );
};

export default MarketInsight;
